/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
/**
 * Provides DOM elements where drag and drop elements should be rendered to
 * and handlers for the onPointerDown, onPointerUp and onPointerMove events.
 * Returns [dragElement, dropElement, handlePointerDown, handlePointerUp, handlePointerMove]
 *
 * @param widgetEditorContext - editor context instance
 * @param createWidget - function that creates a widget, it will be called when drag and drop action ends with a drop on a valid target
 */
export function useDragState(widgetEditorContext, createWidget) {
    const [pointerDown, setPointerDown] = React.useState();
    const [dragElement, setDragElement] = React.useState();
    const [dropElement, setDropElement] = React.useState();
    const [bodyUserSelect, setBodyUserSelect] = React.useState(document.body.style.userSelect);
    const cleanUp = () => {
        if (dragElement) {
            setDragElement(undefined);
        }
        if (dropElement) {
            setDropElement(undefined);
        }
    };
    React.useEffect(() => () => {
        var _a, _b;
        (_a = dragElement === null || dragElement === void 0 ? void 0 : dragElement.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(dragElement);
        (_b = dropElement === null || dropElement === void 0 ? void 0 : dropElement.parentElement) === null || _b === void 0 ? void 0 : _b.removeChild(dropElement);
    }, [dragElement, dropElement]);
    const handlePointerDown = (event) => {
        if (event.button !== 0) {
            return;
        }
        event.target.setPointerCapture(event.pointerId);
        setPointerDown(event.pointerId);
        setBodyUserSelect(document.body.style.userSelect);
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none'; // for Safari v15.0
        cleanUp();
    };
    const handlePointerUp = (event) => {
        if (pointerDown !== event.pointerId || event.button !== 0) {
            return;
        }
        document.body.style.userSelect = bodyUserSelect;
        document.body.style.webkitUserSelect = bodyUserSelect; // for Safari v15.0
        setPointerDown(undefined);
        cleanUp();
        if (dragElement) {
            widgetEditorContext.onDragEnd(createWidget);
        }
    };
    const handlePointerMove = (event) => {
        if (pointerDown !== event.pointerId) {
            return;
        }
        if (!dragElement) {
            const dragDiv = document.createElement('div');
            dragDiv.style.position = 'absolute';
            dragDiv.style.zIndex = '900';
            dragDiv.style.pointerEvents = 'none';
            dragDiv.style.userSelect = 'none';
            dragDiv.style.left = event.pageX + event.width + 4 + 'px';
            dragDiv.style.top = event.pageY + event.height + 16 + 'px';
            document.body.appendChild(dragDiv);
            setDragElement(dragDiv);
            const dropDiv = document.createElement('div');
            dropDiv.style.display = 'none';
            dropDiv.style.width = '100%';
            dropDiv.style.height = '100%';
            dropDiv.style.pointerEvents = 'none';
            dropDiv.style.userSelect = 'none';
            document.body.appendChild(dropDiv);
            setDropElement(dropDiv);
            const moveEditorPath = widgetEditorContext.calculateEditorPath(event.target);
            widgetEditorContext.onDragBegin(dropDiv, moveEditorPath ? event.target : undefined, moveEditorPath);
            widgetEditorContext.onDrag(event.pageX, event.pageY);
        }
        if (dragElement) {
            dragElement.style.left = event.pageX + event.width + 4 + 'px';
            dragElement.style.top = event.pageY + event.height + 16 + 'px';
            widgetEditorContext.onDrag(event.pageX, event.pageY);
        }
    };
    return [dragElement, dropElement, handlePointerDown, handlePointerUp, handlePointerMove];
}
